import DOMPurify from 'dompurify';
import type { Config, DOMPurify as DomPurifyInstance } from 'dompurify';

/** Allowlist khớp BE InputXssGuard (rich HTML). */
const ALLOWED_TAGS = [
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'span',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'img',
];

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'target', 'rel', 'class'];

const DANGEROUS_URL = /^\s*(?:javascript|vbscript|data)\s*:/i;

const purifyConfig: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  // http(s), mailto, relative (# / ./ path) — chặn javascript:/data:/vbscript:
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

let hooksInstalled = false;
let purifyInstance: DomPurifyInstance | null | undefined;

function resolvePurify(): DomPurifyInstance | null {
  if (typeof window === 'undefined') return null;
  if (purifyInstance !== undefined) return purifyInstance;

  const imported = DOMPurify as unknown as DomPurifyInstance & ((w: Window) => DomPurifyInstance);
  let instance: DomPurifyInstance | null = null;

  if (imported && typeof imported.sanitize === 'function' && imported.isSupported) {
    instance = imported;
  } else if (typeof imported === 'function') {
    try {
      instance = imported(window);
    } catch {
      instance = null;
    }
  }

  purifyInstance = instance?.isSupported ? instance : null;
  return purifyInstance;
}

function isSafeHref(href: string): boolean {
  if (!href) return false;
  if (DANGEROUS_URL.test(href)) return false;
  if (/^(https?:|mailto:)/i.test(href)) return true;
  if (/^[/#?]|^\.\.?\//.test(href)) return true;
  // relative path không có scheme
  if (!/^[a-z][a-z0-9+.\-]*:/i.test(href)) return true;
  return false;
}

function ensureHooks(purify: DomPurifyInstance) {
  if (hooksInstalled) return;
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element) || node.tagName !== 'A') return;
    const href = node.getAttribute('href')?.trim() ?? '';
    if (!href || DANGEROUS_URL.test(href)) {
      node.removeAttribute('href');
    }
    // Chỉ gỡ href nguy hiểm / rỗng — giữ http(s)/mailto/relative
    /*if (!isSafeHref(href)) {
      node.removeAttribute('href');
    }*/
  });
  hooksInstalled = true;
}

/** SSR / no-DOM fallback — strip script/on* + javascript: href thô. */
function sanitizeHtmlFallback(dirty: string): string {
  return dirty
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(
      /(<a\b[^>]*\bhref\s*=\s*)(["']?)(?:\s*(?:javascript|vbscript|data)\s*:[^"'\s>]*)\2/gi,
      '$1$2$2',
    )
    .replace(/<a\b(?![^>]*\bhref\s*=)[^>]*>\s*<\/a>/gi, '')
    .replace(/<a\b(?![^>]*\bhref\s*=)[^>]*>([\s\S]*?)<\/a>/gi, '$1');
}

/** Unwrap &lt;a&gt; không còn href hợp lệ / rỗng sau sanitize. */
function unwrapAnchorsWithoutHref(html: string): string {
  if (typeof window === 'undefined' || !html.includes('<a')) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  const anchors = Array.from(template.content.querySelectorAll('a'));
  for (const anchor of anchors) {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (href && !DANGEROUS_URL.test(href)) continue;
    //if (isSafeHref(href)) continue;

    const parent = anchor.parentNode;
    if (!parent) {
      anchor.remove();
      continue;
    }
    while (anchor.firstChild) {
      parent.insertBefore(anchor.firstChild, anchor);
    }
    parent.removeChild(anchor);
  }
  return template.innerHTML;
}

/** Decode entity (tối đa 2 vòng) để bắt payload kiểu `&lt;a href="javascript:..."&gt;`. */
function decodeHtmlEntities(text: string): string {
  if (!text || !/&[a-z#]/i.test(text)) return text;

  if (typeof window !== 'undefined') {
    const textarea = document.createElement('textarea');
    let decoded = text;
    for (let i = 0; i < 2; i += 1) {
      if (!/&[a-z#]/i.test(decoded)) break;
      textarea.innerHTML = decoded;
      const next = textarea.value;
      if (next === decoded) break;
      decoded = next;
    }
    return decoded;
  }

  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
}

/**
 * Sanitize HTML trước khi đưa vào dangerouslySetInnerHTML.
 * - Decode entity nếu có `&lt;...&gt;` (legacy CKEditor / double-encode)
 * - Giữ format editor (p/b/a/img/…)
 * - Cho href http/https/mailto; bỏ javascript:/vbscript:/data:
 * - Unwrap &lt;a&gt; không còn href hợp lệ / rỗng
 */
export function sanitizeHtml(dirty?: string | null): string {
  if (dirty == null || dirty === '') return '';

  // Decode trước khi purify — nếu không, `&lt;a href=javascript:...&gt;` chỉ là text
  // (XSS không chạy) nhưng cũng không xử lý được link thật sau decode.
  const raw = decodeHtmlEntities(String(dirty));
  const purify = resolvePurify();
  if (!purify) return sanitizeHtmlFallback(raw);

  ensureHooks(purify);
  const cleaned = purify.sanitize(raw, purifyConfig);
  return unwrapAnchorsWithoutHref(cleaned);
}

/** Dùng trực tiếp: dangerouslySetInnerHTML={toSafeHtml(html)} */
export function toSafeHtml(dirty?: string | null): { __html: string } {
  return { __html: sanitizeHtml(dirty) };
}
